import React, { useState, useEffect } from 'react';
import { Tenant, TenantType, Property, Unit } from '../types';
import { KUWAIT_LOCATIONS } from '../constants';
import { User, Phone, Mail, FileText, Calendar, Briefcase, Plus, Trash2, Globe, CreditCard, MessageSquare, Upload, Paperclip, Eye, Loader2 } from 'lucide-react';
import { api } from '../services/api';

const openBlob = (dataUrl: string, fileName: string, action: 'view' | 'download') => {
    try {
        const arr = dataUrl.split(',');
        if (arr.length < 2) {
            if (dataUrl.startsWith('http') || dataUrl.startsWith('blob:')) {
                if (action === 'view') {
                    window.open(dataUrl, '_blank');
                } else {
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                return;
            }
            throw new Error("تنسيق الملف غير صالح");
        }
        const mime = arr[0].match(/:(.*?);/)?.[1] || '';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const blob = new Blob([u8arr], { type: mime });
        const url = URL.createObjectURL(blob);
        if (action === 'view') {
            window.open(url, '_blank');
        } else {
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
        alert("❌ تعذر معالجة الملف: " + e.message);
    }
};

interface TenantFormProps {
  onCancel: () => void;
  onSubmit: (data: Tenant) => void;
  preSelectedPropertyId?: string | null;
  initialData?: Tenant | null;
  existingTenants?: Tenant[];
}

const InputGroup = ({ label, children }: { label: string, children: React.ReactNode }) => (
  <div className="mb-5">
    <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
    <div className="relative group">
      {children}
    </div>
  </div>
);

const TenantForm: React.FC<TenantFormProps> = ({ onCancel, onSubmit, preSelectedPropertyId, initialData, existingTenants }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<{propertyId: string, unitId: string, rent: number}[]>([{ propertyId: '', unitId: '', rent: 0 }]);
  const [isExistingTenantMode, setIsExistingTenantMode] = useState(false);
  const [selectedExistingTenantId, setSelectedExistingTenantId] = useState('');
  
  const [attachments, setAttachments] = useState<any[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<{name: string, type: string, data: string}[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  
  const [formData, setFormData] = useState<Partial<Tenant>>({
    type: TenantType.Individual,
    language: 'Arabic',
    hasLegalCase: false,
    sendWelcomeEmail: true,
    sendWelcomeSMS: true,
    rentPaymentDay: 1,
    paymentCycle: 'Monthly'
  });

  useEffect(() => {
    if (isExistingTenantMode && selectedExistingTenantId && existingTenants) {
      const t = existingTenants.find(x => String(x.id) === selectedExistingTenantId);
      if (t) {
        setFormData({
          ...t,
          type: t.type || TenantType.Individual,
          language: t.language || 'Arabic'
        });
        
        const existingUnits = t.assignedUnits?.map(au => ({
            propertyId: String(au.propertyId || ''),
            unitId: String(au.unitId || ''),
            rent: au.rent || 0
        })) || [];
        
        setSelectedUnits([...existingUnits, { propertyId: preSelectedPropertyId || '', unitId: '', rent: 0 }]);
      }
    } else if (!isExistingTenantMode && !initialData) {
        setFormData({
           type: TenantType.Individual,
           language: 'Arabic',
           hasLegalCase: false,
           sendWelcomeEmail: true,
           sendWelcomeSMS: true,
           rentPaymentDay: 1,
           paymentCycle: 'Monthly'
        });
    }
  }, [selectedExistingTenantId, isExistingTenantMode, initialData]); // REMOVED existingTenants to prevent resetting the form on background auto-refresh

  useEffect(() => {
    // Fetch properties to populate dropdowns
    api.properties.getAll().then(props => {
      setProperties(props);
      
      if (initialData) {
        setFormData(initialData);
        if (initialData.assignedUnits && initialData.assignedUnits.length > 0) {
          setSelectedUnits(initialData.assignedUnits.map(au => ({
            propertyId: String(au.propertyId || ''),
            unitId: String(au.unitId || ''),
            rent: au.rent || 0
          })));
        } else if (initialData.assignedUnits?.length === 0) {
          setSelectedUnits([]);
        }
      } else if (preSelectedPropertyId) {
        setSelectedUnits([{ propertyId: preSelectedPropertyId, unitId: '', rent: 0 }]);
      }
    });
  }, [preSelectedPropertyId, initialData]);

  useEffect(() => {
    if (initialData?.id) {
      api.attachments.getByEntity('tenant', String(initialData.id))
        .then(data => setAttachments(data))
        .catch(err => console.error("Error loading attachments", err));
    }
  }, [initialData]);

  const handleUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file count limit
    const totalAttachments = (attachments?.length || 0) + (pendingAttachments?.length || 0);
    if (totalAttachments >= 5) {
      alert("⚠️ لا يمكن إضافة المزيد من المرفقات. الحد الأقصى هو 5 ملفات.");
      return;
    }

    // Check file size
    if (file.size > 5 * 1024 * 1024) {
      alert("⚠️ حجم الملف كبير جداً! يجب ألا يتجاوز حجم المرفق 5 ميجابايت.");
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert("⚠️ صيغة الملف غير مدعومة. يرجى رفع صور (JPG, PNG, WEBP) أو ملفات PDF فقط.");
      return;
    }
    
    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        if (initialData?.id) {
          const uploaded = await api.attachments.upload({
            entity_type: 'tenant',
            entity_id: String(initialData.id),
            file_name: file.name,
            file_type: file.type,
            file_data: reader.result as string
          });
          setAttachments(prev => [...prev, uploaded]);
          alert("✅ تم رفع المرفق بنجاح");
        } else {
          setPendingAttachments(prev => [...prev, {
            name: file.name,
            type: file.type,
            data: reader.result as string
          }]);
          alert("✅ تم إضافة المرفق مؤقتاً وسيجري حفظه مع المستأجر");
        }
      } catch (error: any) {
        alert("❌ فشل رفع المرفق: " + error.message);
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteAttachment = async (id: string, isPending: boolean = false, index?: number) => {
    if (!await window.confirm("هل أنت متأكد من حذف هذا المرفق؟")) return;
    try {
      if (isPending && index !== undefined) {
        setPendingAttachments(prev => prev.filter((_, idx) => idx !== index));
        alert("✅ تم حذف المرفق بنجاح");
      } else {
        await api.attachments.delete(id);
        setAttachments(prev => prev.filter(att => String(att.id) !== String(id)));
        alert("✅ تم حذف المرفق بنجاح");
      }
    } catch (error: any) {
      alert("❌ فشل حذف المرفق: " + error.message);
    }
  };

  const handleViewAttachment = async (id: string, name: string) => {
    try {
      const res = await api.attachments.getData(id);
      if (res && res.file_data) {
        openBlob(res.file_data, name, 'view');
      } else {
        alert("❌ الملف فارغ أو غير موجود");
      }
    } catch (err: any) {
      alert("❌ تعذر عرض المرفق: " + err.message);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleUnitRowChange = (index: number, field: string, value: any) => {
      const newRows = [...selectedUnits];
      newRows[index] = { ...newRows[index], [field]: value };
      
      // Auto-populate rent if unit changes
      if (field === 'unitId') {
          const prop = properties.find(p => String(p.id) === String(newRows[index].propertyId));
          const unit = prop?.units.find(u => String(u.id) === String(value));
          if (unit) {
              newRows[index].rent = unit.rent;
          }
      }
      setSelectedUnits(newRows);
  };

  const addUnitRow = () => {
      setSelectedUnits([...selectedUnits, { propertyId: '', unitId: '', rent: 0 }]);
  };

  const removeUnitRow = (index: number) => {
      const newRows = [...selectedUnits];
      newRows.splice(index, 1);
      setSelectedUnits(newRows);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalData = { 
        ...formData, 
        id: initialData ? initialData.id : (isExistingTenantMode && selectedExistingTenantId ? selectedExistingTenantId : Math.random().toString()),
        assignedUnits: selectedUnits,
        pendingAttachments: pendingAttachments
    } as any;
    onSubmit(finalData);
  };
  
  // Styles (Blue/Gray theme)
  const inputClass = "block w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm";
  const selectClass = "block w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm appearance-none";

  return (
    <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-white p-5 border-b border-slate-200 flex justify-between items-center shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-800">{initialData ? 'تعديل بيانات المستأجر' : 'تسجيل مستأجر جديد'}</h2>
          <p className="text-slate-500 text-[10px] mt-1">البيانات الشخصية وتفاصيل الوحدات</p>
        </div>
      </div>

      <div className="p-4 sm:p-8 space-y-6">
        {/* Section 1: Personal Info */}
        <section className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm">
             <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="bg-fly-50 p-2 rounded-lg">
                    <User className="text-fly-600 w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-slate-800">معلومات المستأجر</h3>
             </div>

             {!initialData && existingTenants && existingTenants.length > 0 && preSelectedPropertyId && (
                 <div className="mb-6 bg-slate-50 p-2 rounded-xl flex gap-2">
                     <button
                         type="button"
                         onClick={() => { setIsExistingTenantMode(false); setSelectedExistingTenantId(''); }}
                         className={`flex-1 py-2 font-bold text-sm rounded-lg transition-all ${!isExistingTenantMode ? 'bg-white shadow-sm text-fly-600' : 'text-slate-500 hover:text-slate-700'}`}
                     >
                         تسجيل مستأجر جديد
                     </button>
                     <button
                         type="button"
                         onClick={() => setIsExistingTenantMode(true)}
                         className={`flex-1 py-2 font-bold text-sm rounded-lg transition-all ${isExistingTenantMode ? 'bg-white shadow-sm text-fly-600' : 'text-slate-500 hover:text-slate-700'}`}
                     >
                         اختيار مستأجر مسجل
                     </button>
                 </div>
             )}

             {isExistingTenantMode ? (
                 <div className="mb-6">
                     <label className="block text-sm font-bold text-slate-700 mb-2">ابحث واختر المستأجر *</label>
                     <select 
                         className={selectClass}
                         required={isExistingTenantMode}
                         value={selectedExistingTenantId}
                         onChange={(e) => setSelectedExistingTenantId(e.target.value)}
                     >
                         <option value="">-- اختر مستأجر --</option>
                         {existingTenants?.map(et => (
                             <option key={et.id} value={et.id}>{et.firstName} {et.lastName} - {et.mobile}</option>
                         ))}
                     </select>
                 </div>
             ) : null}

            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 ${isExistingTenantMode && !selectedExistingTenantId ? 'opacity-50 pointer-events-none' : ''}`}>
                 <div className="lg:col-span-3">
                    <label className="block text-sm font-bold text-slate-700 mb-2">نوع المستأجر *</label>
                    <div className="flex space-x-6 space-x-reverse">
                        <label className="flex items-center cursor-pointer p-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all has-[:checked]:border-fly-500 has-[:checked]:bg-fly-50">
                            <input type="radio" name="type" value={TenantType.Individual} checked={formData.type === TenantType.Individual} onChange={handleInputChange} className="ml-2" />
                            <span className="text-sm font-bold text-slate-700">فرد</span>
                        </label>
                        <label className="flex items-center cursor-pointer p-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all has-[:checked]:border-fly-500 has-[:checked]:bg-fly-50">
                            <input type="radio" name="type" value={TenantType.Company} checked={formData.type === TenantType.Company} onChange={handleInputChange} className="ml-2" />
                            <span className="text-sm font-bold text-slate-700">شركة</span>
                        </label>
                    </div>
                </div>

                <InputGroup label="الإسم الأول *">
                    <input name="firstName" required value={formData.firstName || ''} onChange={handleInputChange} className={inputClass} placeholder="الإسم الأول" />
                </InputGroup>
                
                <InputGroup label="الإسم الأخير *">
                    <input name="lastName" required value={formData.lastName || ''} onChange={handleInputChange} className={inputClass} placeholder="الإسم الأخير" />
                </InputGroup>

                <InputGroup label="رقم الهاتف *">
                    <input name="mobile" required value={formData.mobile || ''} placeholder="9xxxxxxx" onChange={handleInputChange} className={inputClass} />
                </InputGroup>

                <InputGroup label="البريد الإلكتروني">
                    <input type="email" name="email" value={formData.email || ''} onChange={handleInputChange} className={inputClass} placeholder="name@example.com" />
                </InputGroup>

                <InputGroup label="اللغة المفضلة *">
                    <select name="language" value={formData.language || 'Arabic'} onChange={handleInputChange} className={selectClass}>
                        <option value="Arabic">العربية</option>
                        <option value="English">English</option>
                    </select>
                </InputGroup>

                <InputGroup label="رقم البطاقة المدنية">
                    <input name="civilId" value={formData.civilId || ''} onChange={handleInputChange} className={inputClass} />
                </InputGroup>
            </div>
        </section>

        {/* Section 2: Contract Info */}
        <section className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm">
             <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="bg-blue-50 p-2 rounded-lg">
                    <Calendar className="text-blue-600 w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-slate-800">بيانات العقد والدفع</h3>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <InputGroup label="تاريخ بداية العقد (Contract Start)">
                    <input type="date" lang="en" name="contractStart" value={formData.contractStart || ''} onChange={handleInputChange} className={`${inputClass} text-center`} dir="ltr" />
                </InputGroup>
                 
                <InputGroup label="تاريخ إنتهاء العقد (Contract End)">
                    <input type="date" lang="en" name="contractEnd" value={formData.contractEnd || ''} onChange={handleInputChange} className={`${inputClass} text-center`} dir="ltr" />
                </InputGroup>

                <InputGroup label="دورة الدفع">
                    <select name="paymentCycle" value={formData.paymentCycle || 'Monthly'} onChange={handleInputChange} className={selectClass}>
                        <option value="Monthly">شهري</option>
                        <option value="Quarterly">ربع سنوي</option>
                        <option value="Yearly">سنوي</option>
                    </select>
                </InputGroup>

                <InputGroup label="يوم استحقاق/إصدار الفاتورة *">
                    <select name="rentPaymentDay" value={formData.rentPaymentDay || 1} onChange={handleInputChange} className={selectClass} required>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>{day} من الشهر</option>
                        ))}
                    </select>
                </InputGroup>
                
                 <div className="md:col-span-3 flex flex-wrap gap-3 pt-2">
                     <label className="flex items-center cursor-pointer p-3 border border-red-200 bg-red-50 rounded-xl hover:bg-red-100 transition-colors">
                        <input type="checkbox" name="hasLegalCase" checked={formData.hasLegalCase} onChange={handleInputChange} className="ml-3 w-4 h-4 text-red-600 rounded focus:ring-red-500" />
                        <span className="text-red-800 text-sm font-bold">يوجد قضية قانونية؟</span>
                    </label>
                    <label className="flex items-center cursor-pointer p-3 border border-slate-200 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                        <input type="checkbox" name="sendWelcomeEmail" checked={formData.sendWelcomeEmail} onChange={handleInputChange} className="ml-3 w-4 h-4 text-fly-600 rounded focus:ring-fly-500" />
                        <span className="text-slate-800 text-sm font-bold">إرسال بريد ترحيب؟</span>
                    </label>
                </div>
             </div>
        </section>

        {/* Section 3: Units (Dynamic Rows) */}
        <section className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                    <div className="bg-purple-50 p-2 rounded-lg">
                        <Briefcase className="text-purple-600 w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">معلومات الوحدات</h3>
                </div>
                <button type="button" onClick={addUnitRow} className="flex items-center gap-2 px-3 py-1.5 bg-fly-600 text-white rounded-lg text-xs font-bold hover:bg-fly-700">
                    <Plus size={14} /> إضافة وحدة
                </button>
            </div>
            
            <div className="space-y-3">
                {selectedUnits.map((row, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="md:col-span-4">
                            <label className="block text-[10px] font-bold text-slate-500 mb-1">العقار</label>
                            <select 
                                value={row.propertyId} 
                                onChange={(e) => handleUnitRowChange(index, 'propertyId', e.target.value)}
                                className={selectClass}
                            >
                                <option value="">إختر العقار</option>
                                {properties.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-3">
                            <label className="block text-[10px] font-bold text-slate-500 mb-1">الوحدة</label>
                            <select 
                                value={row.unitId} 
                                onChange={(e) => handleUnitRowChange(index, 'unitId', e.target.value)}
                                className={selectClass}
                                disabled={!row.propertyId}
                            >
                                <option value="">إختر الوحدة</option>
                                {properties.find(p => String(p.id) === String(row.propertyId))?.units
                                    .filter(u => !u.tenantId || (initialData?.id && String(u.tenantId) === String(initialData.id)) || (selectedExistingTenantId && String(u.tenantId) === selectedExistingTenantId))
                                    .map(u => (
                                    <option key={u.id} value={u.id}>{u.unitNumber} ({u.type})</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-4">
                             <label className="block text-[10px] font-bold text-slate-500 mb-1">الإيجار (د.ك)</label>
                             <input 
                                type="number" 
                                min="0"
                                step="0.001"
                                value={row.rent || ''} 
                                onChange={(e) => handleUnitRowChange(index, 'rent', Number(e.target.value))}
                                className={inputClass}
                                placeholder="الإيجار" 
                             />
                        </div>
                        <div className="md:col-span-1">
                             <button 
                                type="button" 
                                onClick={() => removeUnitRow(index)}
                                className="w-full py-2.5 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 flex justify-center items-center"
                                title="إزالة (إخلاء)"
                             >
                                <Trash2 size={18} />
                             </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>

        {/* قسم مرفقات العقد */}
        <section className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                        <Paperclip className="text-slate-600 w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">مرفقات العقد (الصور والمستندات)</h3>
                </div>
                <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold cursor-pointer transition-all">
                    {isUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    <span>رفع مستند / صورة</span>
                    <input 
                        type="file" 
                        accept="image/*,application/pdf" 
                        onChange={handleUploadAttachment} 
                        className="hidden"
                        disabled={isUploading}
                    />
                </label>
            </div>
            
            {(attachments.length > 0 || pendingAttachments.length > 0) ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* المرفقات المرفوعة بالفعل */}
                    {attachments.map((att) => (
                        <div key={att.id} className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
                            <div className="flex items-center gap-3 truncate">
                                <div className="bg-white p-2 rounded-lg border border-slate-200 text-slate-400 shrink-0">
                                    <FileText size={16} />
                                </div>
                                <div className="truncate">
                                    <div className="text-xs font-bold text-slate-800 truncate" title={att.file_name}>{att.file_name}</div>
                                    <div className="text-[9px] text-slate-400 font-mono mt-0.5">{new Date(att.uploaded_at || att.uploadedAt || Date.now()).toLocaleDateString('en-GB')}</div>
                                </div>
                            </div>
                            <div className="flex gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => handleViewAttachment(att.id, att.file_name)}
                                    className="p-2 bg-white text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                    title="عرض المستند"
                                >
                                    <Eye size={14} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteAttachment(att.id, false)}
                                    className="p-2 bg-white text-red-500 border border-slate-200 rounded-lg hover:bg-red-50 hover:text-red-650 transition-colors"
                                    title="حذف المستند"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                    
                    {/* المرفقات المعلقة (أثناء الإنشاء) */}
                    {pendingAttachments.map((att, idx) => (
                        <div key={`pending-${idx}`} className="flex items-center justify-between p-3.5 bg-blue-50/50 border border-blue-100 rounded-xl">
                            <div className="flex items-center gap-3 truncate">
                                <div className="bg-white p-2 rounded-lg border border-blue-200 text-blue-400 shrink-0">
                                    <Paperclip size={16} className="text-blue-500" />
                                </div>
                                <div className="truncate">
                                    <div className="text-xs font-bold text-blue-800 truncate" title={att.name}>{att.name}</div>
                                    <div className="text-[9px] text-blue-400 font-bold mt-0.5">معلق (بانتظار الحفظ)</div>
                                </div>
                            </div>
                            <div className="flex gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => openBlob(att.data, att.name, 'view')}
                                    className="p-2 bg-white text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                    title="عرض المؤقت"
                                >
                                    <Eye size={14} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteAttachment('', true, idx)}
                                    className="p-2 bg-white text-red-500 border border-slate-200 rounded-lg hover:bg-red-50 hover:text-red-650 transition-colors"
                                    title="إزالة المؤقت"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-6 bg-slate-50/50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-xs">
                    لا توجد مرفقات مرفوعة حالياً لهذا العقد. يمكنك رفع صور العقد أو ملفات PDF.
                </div>
            )}
        </section>
      </div>

      <div className="p-4 sm:p-5 bg-white border-t border-slate-200 flex flex-wrap sm:flex-nowrap justify-center gap-3 sm:gap-4 shrink-0">
        <button type="button" onClick={onCancel} className="w-full sm:w-auto px-8 py-3 bg-white border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-sm active:scale-95">إلغاء</button>
        <button type="submit" className="w-full sm:w-auto px-12 py-3 bg-fly-600 text-white font-bold rounded-xl hover:bg-fly-700 shadow-lg shadow-fly-600/20 transition-all text-sm active:scale-95">حفظ البيانات</button>
      </div>
    </form>
  );
};

export default TenantForm;